import type { Position } from 'geojson';
import { around } from 'geokdbush';
import type { Immutable } from 'immer';
import KDBush from 'kdbush';
import { type DeviceStateFlag, Targets } from '../consts/events';
import { formatJSONTime, formatTimeDuration } from '../formattings/times';
import { formatAsKM } from '../formattings/units';
import { calculateDistanceInMeter } from '../point_utils';
import { floorTo } from '../roundTo';
import type { RacemapEvent, RacemapStarter, Tags } from '../types/events';
import type { SplitObject } from '../types/geos';
import type { LatLng } from '../types/trackping';
import type { PointWithOffset } from '../types/types';
import type { ID } from '../types/utils';
export type { TagsCollection } from '@racemap/sdk/schema/starters';
import { DISCONNECT_REASON } from '@racemap/sdk/schema/trackers';
import { Unit } from '../types/units';
import type { PointWithOffsetTimeNumber, PointWithTrackOffset } from './codec';

export type ExtendedStarterTrack = Array<ExtendedStarterTrackPoint>;

export type ExtendedStarterTracks = Map<ID, ExtendedStarterTrack>;

export type PointWithTimeOffset = PointWithOffset & {
  timeOffset?: number;
  relativeDelta?: number;
  geofenceId?: string;
  manualDuration?: number;
  distanceToShadowTrack?: number;
};

export type PointWithSpeedAndOffset = PointWithTimeOffset & {
  speed: number; // Its used as meters per millisecond
  forceFinish?: boolean;
  splitTimeError?: number;
  splitOffsetError?: number;
  isBreak?: boolean;
};

export interface RefinedPoint extends Omit<PointWithTrackOffset, 'time'> {
  time: Date;
  speed: number;
  timeOffset: number;
  isBreak: boolean;
}

export type ExtendedStarterTrackPoint = RefinedPoint & {
  distanceToShadowTrack: number;
};

export type Geofence = {
  name: string;
  offset?: number;
  timeOffset?: number;
  id?: string;
  position?: Position;
  thresholds?: Thresholds;
};

export type Thresholds = {
  latHigh: number;
  latLow: number;
  lngHigh: number;
  lngLow: number;
};

export interface TimeAtGeofence {
  id: string;
  relativeProgress: number;
  time: number;
  relativeDeltaProgress?: number;
}

export interface StarterInfo {
  id: ID;
  name: string;
  trackId: ID;
  startNumber: string;
  currentSpeed: number;
  tags: Tags | null;
  markerColor: string;
  rank: number;
  state: DeviceStateFlag | null;
  progress: number;
  activeDuration: number | null;
  results: Array<TimeAtGeofence>;
  online: boolean;
  manualFinishDuration: number;
  currentDuration: number | null;
}

export interface PreparedStarterInfo extends StarterInfo {
  tagsRank?: Record<string, number>;
  restDuration: number;
}

export interface RanksPackage {
  name: string;
  startTime: string;
  endTime: string;
  geoJSON: string;
  location: string;
  image: string;
  timekeepings: Array<Geofence>;
  starters: Array<StarterInfo>;
}

export interface PreparedRanksPackage extends RanksPackage {
  tagsStarterCount: Map<string, Map<string, number>>;
  starters: Array<PreparedStarterInfo>;
}

export enum RanksQueryFrom {
  MONITOR = 'MONITOR',
  LEADERBOARD = 'LEADERBOARD',
  APP = 'APP',
}

/**
 * Indexes a shadow track once and returns the distance in meter from a point to
 * its nearest shadow track vertex. Shadow track entries are in GeoJSON order,
 * `[lng, lat]` (a third entry, the elevation, is ignored). An empty shadow track
 * measures 0 for every point, the same as having no shadow track at all.
 */
export function buildShadowTrackDistance(
  shadowTrack: Array<LatLng>,
): (point: { lat: number; lng: number }) => number {
  if (shadowTrack.length === 0) return () => 0;

  const index = new KDBush(shadowTrack.length);
  for (const [lng, lat] of shadowTrack) {
    index.add(lng, lat);
  }
  index.finish();

  return (point) => {
    const [nearest] = around(index, point.lng, point.lat, 1);
    const [lng, lat] = shadowTrack[nearest];
    return calculateDistanceInMeter(lat, lng, point.lat, point.lng);
  };
}

export function getPseudoDeviceStateForRepeatEvent(
  starter: Immutable<RacemapStarter>,
  lastPoint: PointWithOffset | null,
  progress: number,
): RacemapStarter['deviceState'] {
  const battery = Math.round(progress != null ? (1 - progress * 0.8) * 100 : 100);
  // if the last point is not null and the time of the last point is less than 5 minutes ago
  const isOnline = !!(
    lastPoint != null && new Date(lastPoint.time).getTime() > Date.now() - 300000
  );

  const baseState = {
    ...(starter.deviceState ?? {}),
    battery,
  };

  const previousReason = starter.deviceState?.disconnectReason;

  //  depending on isOnline, we set lastConnectedAt or lastDisconnectedAt, resulting in a valid starter online state
  if (isOnline) {
    return {
      ...baseState,
      lastConnectedAt: new Date(),
      disconnectReason: previousReason ?? DISCONNECT_REASON.NONE,
    };
  }
  return {
    ...baseState,
    lastDisconnectedAt: new Date(),
    disconnectReason: DISCONNECT_REASON.NO_RECENT_LOCATION_UPDATE,
  };
}

export function sortTimes(a: number | null | undefined, b: number | null | undefined): number {
  if (a == null || Number.isNaN(a)) {
    if (b == null || Number.isNaN(b)) {
      return 0;
    }
    return 1;
  }
  if (b == null || Number.isNaN(b)) {
    return -1;
  }
  return a - b;
}

export function testEventHasVirtDuration(event: Immutable<RacemapEvent>) {
  return (
    (!eventHasShadowtrack(event) || event.modules.projection.enabled) &&
    event.modules.timing.target === Targets.DURATION &&
    event.modules.timing.duration != null
  );
}

export function testEventHasVirtDistance(event: Immutable<RacemapEvent>) {
  return (
    (!eventHasShadowtrack(event) || event.modules.projection.enabled) &&
    event.modules.timing.target === Targets.DISTANCE &&
    event.modules.timing.distance != null
  );
}

export function getVirtualRaceDistance(
  event: Immutable<RacemapEvent>,
  splits: Immutable<Array<SplitObject>>,
): number {
  const timekeepingSplits = splits?.filter((split) => split.properties.timekeeping);
  const withShadowtrack = eventHasShadowtrack(event);

  if (withShadowtrack && timekeepingSplits?.length > 0) {
    const lastTimekeepingSplit = timekeepingSplits?.[timekeepingSplits.length - 1];
    const firstTimekeepingSplit = timekeepingSplits?.[0];

    return (
      (lastTimekeepingSplit.properties.offset || 0) - (firstTimekeepingSplit.properties.offset || 0)
    );
  }

  if (withShadowtrack && splits.length > 0) {
    const lastSplit = splits[splits.length - 1];
    if (lastSplit.properties.offset == null) throw new Error("Offset shouldn't be null");

    return lastSplit.properties.offset;
  }

  return event.modules.timing.distance;
}

export function getVirtualRaceDuration(event: Immutable<RacemapEvent>) {
  return event?.modules?.timing?.duration;
}

export function eventHasShadowtrack(event: Immutable<RacemapEvent>) {
  return event.geo != null && event.geo.shadowgeojsonHash != null;
}

export function buildCSVFile(
  tableData: Array<StarterInfo | null>,
  geofences: Array<Geofence>,
  eventHasVirtDuration: boolean,
) {
  return new Blob(
    [
      Array.from(
        (function* () {
          yield `ID;Name;Start Number;Rank;Current Progress (${
            eventHasVirtDuration ? 'hh:mm:ss' : Unit.Distance.km
          });${geofences
            .map(
              (g) =>
                `${g.name} (${
                  eventHasVirtDuration
                    ? formatTimeDuration(g.timeOffset)
                    : formatAsKM({ meters: g.offset })
                }) Split;${g.name} (${formatAsKM({ meters: g.offset })}) Timestamp`,
            )
            .join(';')}`;
          for (const starterData of tableData) {
            if (starterData == null) continue;
            yield [
              starterData.id,
              starterData.name,
              starterData.startNumber,
              starterData.rank,
              floorTo(starterData.progress / 1000, 2),
              ...starterData.results
                .slice(0)
                .map((time) =>
                  time != null
                    ? `${
                        eventHasVirtDuration
                          ? formatAsKM({ meters: time.relativeProgress })
                          : formatTimeDuration(time.relativeProgress)
                      };${formatJSONTime(time.time) || ''}`
                    : ';',
                ),
            ].join(';');
          }
        })(),
      ).join('\n'),
    ],
    {
      type: 'text/csv;charset=utf-8;',
    },
  );
}

export function buildGPXFile(pointsMap: Record<string, Array<PointWithOffsetTimeNumber>>): Blob {
  let tracks = '';

  for (const [eventName, points] of Object.entries(pointsMap)) {
    tracks += `<trk>
    <name>${eventName}</name>
    <trkseg>${points
      .map(
        (p) => `
      <trkpt lat="${p.lat}" lon="${p.lng}">
        <time>${new Date(p.time).toISOString()}</time>
      </trkpt>`,
      )
      .join('')}
    </trkseg>
  </trk>`;
  }

  return new Blob(
    [
      `<?xml version="1.0" encoding="UTF-8" standalone="no" ?>
<gpx version="1.1" creator="Racemap">
  <metadata><time>${new Date().toISOString()}</time></metadata>
  ${tracks}
</gpx>
`,
    ],
    { type: 'application/gpx+xml' },
  );
}

/**
 * Enriches each entry in a track with distances to the shadow track.
 */
export function enrichTrackWithShadowDistances<
  T extends { lat: number; lng: number; isOnTrack?: boolean },
>(
  track: Map<ID, Array<T>>,
  shadowTrack?: Array<LatLng> | null,
): Map<ID, Array<T & { distanceToShadowTrack: number }>> {
  const enriched: Map<ID, Array<T & { distanceToShadowTrack: number }>> = new Map();
  if (shadowTrack == null) {
    for (const [id, points] of track.entries()) {
      enriched.set(
        id,
        points.map((p) => ({ ...p, distanceToShadowTrack: 0 })),
      );
    }
    return enriched;
  }

  //TODO: Only recreate the KDBush if the shadowtrack changes.
  // build spatial index once
  const distanceTo = buildShadowTrackDistance(shadowTrack);
  for (const [id, pts] of track.entries()) {
    const enrichedPts = pts.map((point) => {
      const distanceToShadowTrack = point.isOnTrack === false ? distanceTo(point) : 0;
      return {
        ...point,
        distanceToShadowTrack,
      };
    });
    enriched.set(id, enrichedPts);
  }
  return enriched;
}
